import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CurrencyEur, Storefront, Warning, Clock, CreditCard, TrendUp, DeviceMobile, ArrowsClockwise, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import { saCard, fmtD, SABadge, ACCOUNT_STATUS } from "../../components/saShared";

export default function SADashboard() {
  const [d, setD] = useState(null);
  const [sc, setSc] = useState(null);
  const [sys, setSys] = useState(null);
  const [updating, setUpdating] = useState(false);

  const loadSys = () => api.get("/superadmin/system/info").then((r) => setSys(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/superadmin/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/superadmin/platform/sms-credits").then((r) => setSc(r.data)).catch(() => {});
    loadSys();
  }, []);

  const downloadBackup = async () => {
    try {
      const r = await api.get("/superadmin/system/backup/latest", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = (r.headers["content-disposition"] || "").match(/filename="?([^";]+)/)?.[1] || "dishenda-backup.tar.gz";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Backup scaricato");
    } catch { toast.error("Nessun backup disponibile"); }
  };

  const doUpdate = async () => {
    if (!window.confirm("Scaricare e installare l'ultima versione da GitHub? Prima viene fatto un backup di sicurezza del database.")) return;
    setUpdating(true);
    try {
      const r = await api.post("/superadmin/system/update");
      if (r.data.ok) {
        toast.success("Applicazione aggiornata all'ultima versione");
        loadSys();
      } else {
        toast.error("Aggiornamento fallito: rollback automatico eseguito");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aggiornamento non disponibile in questo ambiente");
    } finally { setUpdating(false); }
  };

  if (!d) return <div className="text-sm text-zinc-500 py-10">Caricamento...</div>;

  const metrics = [
    { l: "Ristoranti totali", v: d.ristoranti_totali, icon: Storefront, t: "sa-m-totali" },
    { l: "Account attivi", v: d.attivi, icon: TrendUp, t: "sa-m-attivi" },
    { l: "In prova", v: d.trial, icon: Clock, t: "sa-m-trial" },
    { l: "Sospesi", v: d.sospesi, icon: Warning, t: "sa-m-sospesi", danger: d.sospesi > 0 },
    { l: "Abbonamenti attivi", v: d.abbonamenti_attivi, icon: CreditCard, t: "sa-m-abbonamenti" },
    { l: "Pagamenti falliti", v: d.pagamenti_falliti, icon: Warning, t: "sa-m-falliti", danger: d.pagamenti_falliti > 0 },
    { l: "In tolleranza", v: d.in_tolleranza + d.pagamento_fallito, icon: Clock, t: "sa-m-tolleranza", warn: (d.in_tolleranza + d.pagamento_fallito) > 0 },
    { l: "Crediti SMS Brevo", v: sc ? (sc.credits === null ? "n/d" : sc.credits.toLocaleString("it-IT")) : "…",
      icon: DeviceMobile, t: "sa-m-sms-credits",
      danger: !!sc && sc.credits !== null && sc.credits < sc.threshold,
      warn: !!sc && sc.credits !== null && sc.credits >= sc.threshold && sc.credits < sc.threshold * 3 },
  ];

  return (
    <div className="space-y-6" data-testid="sa-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">Dashboard piattaforma</h1>
          <p className="text-sm text-zinc-500">Stato account, abbonamenti e fatturazione</p>
        </div>
        <div className={`${saCard} px-5 py-3`} data-testid="sa-mrr">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1">
            <CurrencyEur size={12} /> MRR
          </div>
          <div className="font-display text-3xl font-black text-amber-400">€{d.mrr.toLocaleString("it-IT")}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {metrics.map(({ l, v, icon: Icon, t, danger, warn }) => (
          <div key={l} className={`${saCard} p-4 ${danger ? "border-rose-500/50" : warn ? "border-amber-500/50" : ""}`} data-testid={t}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-zinc-500">
              <Icon size={12} weight="bold" /> {l}
            </div>
            <div className="font-display text-2xl sm:text-3xl font-black text-white mt-1">{v}</div>
          </div>
        ))}
      </div>

      <section className={saCard} data-testid="sa-sistema">
        <h2 className="px-4 py-3 border-b border-zinc-800 font-display font-semibold text-white text-sm flex items-center gap-2">
          <ArrowsClockwise size={14} /> Sistema e aggiornamenti
        </h2>
        <div className="p-4 space-y-2.5 text-xs text-zinc-400">
          {!sys ? <p>Caricamento...</p> : (
            <>
              <div className="flex justify-between gap-3">
                <span>Versione installata</span>
                <span className="font-mono text-zinc-200" data-testid="sa-sys-version">{sys.version || "n/d"}{sys.branch ? ` (${sys.branch})` : ""}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span>Ultimo backup database</span>
                <span className="text-zinc-200" data-testid="sa-sys-backup">{sys.ultimo_backup || "nessuno"}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span>Backup conservati (rotazione 30)</span>
                <span className="text-zinc-200">{sys.backup_totali}</span>
              </div>
              <div className="flex gap-2 pt-2 flex-wrap">
                <button onClick={downloadBackup} data-testid="sa-backup-download"
                  className="h-8 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-xs font-semibold text-zinc-200 flex items-center gap-1.5 hover:bg-zinc-700 transition-colors">
                  <DownloadSimple size={13} /> Scarica ultimo backup
                </button>
                <button onClick={doUpdate} disabled={!sys.self_hosted || updating} data-testid="sa-system-update"
                  title={sys.self_hosted ? "" : "Disponibile solo sull'installazione VPS (self-hosted)"}
                  className="h-8 px-3 rounded-md bg-amber-400 text-zinc-950 text-xs font-bold flex items-center gap-1.5 hover:bg-amber-300 active:scale-95 transition disabled:opacity-40">
                  <ArrowsClockwise size={13} weight="bold" /> {updating ? "Aggiornamento..." : "Aggiorna applicazione"}
                </button>
              </div>
              {!sys.self_hosted && (
                <p className="text-[10px] text-zinc-600 pt-1">
                  L'aggiornamento con un click si attiva sull'installazione VPS (self-hosted): su questo ambiente
                  gli aggiornamenti passano dal deploy della piattaforma.
                </p>
              )}
            </>
          )}
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-5">
        <section className={saCard}>
          <h2 className="px-4 py-3 border-b border-zinc-800 font-display font-semibold text-white text-sm">Prossime scadenze / rinnovi</h2>
          <div className="divide-y divide-zinc-800/60">
            {d.prossime_scadenze.length === 0 && <div className="px-4 py-6 text-sm text-zinc-500">Nessuna scadenza nei prossimi 14 giorni</div>}
            {d.prossime_scadenze.map((r) => (
              <Link key={r.id} to={`/superadmin/ristoranti/${r.id}`} data-testid={`sa-renewal-${r.id}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-800/50 transition-colors">
                <span className="flex-1 text-sm font-medium text-zinc-100">{r.name}</span>
                <span className="text-xs text-zinc-500">{r.plan_name}</span>
                <span className="text-xs font-semibold text-amber-400">{fmtD(r.next_payment_at)}</span>
              </Link>
            ))}
          </div>
        </section>
        <section className={saCard}>
          <h2 className="px-4 py-3 border-b border-zinc-800 font-display font-semibold text-white text-sm">Account in tolleranza / pagamento fallito</h2>
          <div className="divide-y divide-zinc-800/60">
            {d.in_tolleranza_list.length === 0 && <div className="px-4 py-6 text-sm text-zinc-500">Nessun account in tolleranza</div>}
            {d.in_tolleranza_list.map((r) => (
              <Link key={r.id} to={`/superadmin/ristoranti/${r.id}`} data-testid={`sa-grace-${r.id}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-800/50 transition-colors">
                <span className="flex-1 text-sm font-medium text-zinc-100">{r.name}</span>
                <SABadge map={ACCOUNT_STATUS} value="tolleranza" />
                <span className="text-xs text-zinc-500">scade {fmtD(r.grace_ends_at)}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
